import type { Point, Text } from 'pixi.js';
import { Container } from 'pixi.js';
import { getLocalizedValue } from '@softgames/localization';
import { applyLayoutState } from '../../../../libs/layouts/populateLayouts/applyLayoutState';
import { WinScreenRewardsLayout } from '../../../../../layouts/winScreen/WinScreenRewardsLayout';
import { playLayoutAnimation } from '../../../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { PiggyBankTier } from '../../../../gameEvents/piggyBank/types';
import { PiggyBankMoodView, PiggyBankMood } from '../../../../gameEvents/piggyBank/views/PiggyBankMoodView';

export enum RewardType {
  Coins = 'Coins',
  Tiles = 'Tiles',
  PiggyBank = 'PiggyBank',
  PiggyBankFull = 'PiggyBankFull',
}

interface RewardViewConfig {
  type: RewardType;
  amount: number;
}

export class WinPopupRewardView extends Container {
  private readonly layout = new WinScreenRewardsLayout();

  private readonly moodView: PiggyBankMoodView;

  private amount: number;

  constructor(private readonly config: RewardViewConfig) {
    super();

    this.amount = this.config.amount;

    const { root, piggyBankSlot } = this.layout;
    this.addChild(root);
    this.setAmountText(this.config.amount);

    this.moodView = new PiggyBankMoodView();
    this.moodView.props({
      parent: piggyBankSlot,
    });

    void playLayoutAnimation(this.layout, 'Rewards_Animation', true);

    this.setType(config.type);
  }

  public async playAppearAnimation(): Promise<void> {
    await playLayoutAnimation(this.layout, 'Appear');
  }

  public async playPiggyBankFullAnimation(): Promise<void> {
    await playLayoutAnimation(this.layout, 'PiggyBank_Full');
  }

  public setPiggyBankTier(tier: PiggyBankTier): void {
    if (![PiggyBankTier.Legacy, PiggyBankTier.Normal].includes(tier)) {
      this.moodView.setTier(tier);
    }
  }

  public getDisplayedAmount(): number {
    return this.amount;
  }

  public getGlobalPartStartPoint(): Point {
    return this.layout.particleStartPoint.getGlobalPosition();
  }

  public getType(): RewardType {
    return this.config.type;
  }

  public setType(type: RewardType): void {
    switch (type) {
      case RewardType.Coins:
        applyLayoutState(this.layout, 'coin');
        break;
      case RewardType.PiggyBank:
        applyLayoutState(this.layout, 'piggyBank');
        this.moodView.setIconState(PiggyBankMood.Normal);
        break;
      case RewardType.PiggyBankFull:
        applyLayoutState(this.layout, 'piggyBankFull');
        this.moodView.setIconState(PiggyBankMood.Sorry);
        break;
      default:
        break;
    }
  }

  public getAmountText(): Text {
    return this.layout.rewardAmountText;
  }

  public setAmountText(amount: number): void {
    this.amount = amount;
    const formattedAmount = getLocalizedValue(amount);
    this.layout.rewardAmountText.text = `x${formattedAmount}`;
  }

  public async displayOldAmountAnimation(amount: number): Promise<void> {
    this.layout.oldRewardAmountText.text = `x${getLocalizedValue(amount)}`;
    await playLayoutAnimation(this.layout, 'Show_Old_Amount');
  }
}
